import React, { useEffect, useMemo, useState } from "react";
import { Newspaper } from "lucide-react";
import api from "@/lib/api";

const LABEL = { wikipedia: "Wikipedia", guardian: "The Guardian", nyt: "The New York Times" };

export const HeadlineTicker = ({ ddmm }) => {
  const [items, setItems] = useState([]);
  const mmdd = ddmm ? `${ddmm.slice(3, 5)}-${ddmm.slice(0, 2)}` : null;
  const newsPath = ddmm ? `/oddlytrue-news/${parseInt(ddmm.slice(3, 5), 10)}/${parseInt(ddmm.slice(0, 2), 10)}` : "/oddlytrue-news/today";

  useEffect(() => {
    let alive = true;
    Promise.allSettled([api.get("/onthisday", { params: { ...(mmdd ? { date: mmdd } : {}), full: true } }), api.get(newsPath)]).then(([otd, news]) => {
      if (!alive) return;
      const out = [];
      const h = news.status === "fulfilled" ? news.value.data.hallOfFame : null;
      if (h) out.push({ key: "hof", year: h.year, text: h.headline, source: h.newspaper || "Hall of Fame", star: true });
      if (otd.status === "fulfilled") {
        const d = otd.value.data;
        (d.nyt || []).forEach((n, i) => out.push({ key: `nyt-${i}`, year: n.year, text: n.headline, source: LABEL.nyt, url: n.url }));
        (d.guardian || []).forEach((g, i) => out.push({ key: `g-${i}`, year: g.year, text: g.headline, source: LABEL.guardian, url: g.url }));
        (d.picks || []).forEach((p, i) => out.push({ key: `w-${i}`, year: p.year, text: p.text, source: LABEL.wikipedia, url: p.url || p.page?.url }));
      }
      setItems(out);
    });
    return () => { alive = false; };
  }, [mmdd, newsPath]);

  const loop = useMemo(() => [...items, ...items], [items]);
  if (items.length === 0) return null;
  const duration = Math.max(40, items.length * 9);

  return (
    <div data-testid="headline-ticker" className="mt-6 mb-10 border-y-2 border-ink bg-[#fbf8f1] overflow-hidden group" aria-label="Today's headlines">
      <div className="flex items-stretch">
        <div className="shrink-0 flex items-center gap-2 px-4 bg-ink text-gold text-[10px] font-bold uppercase tracking-[0.2em]"><Newspaper className="w-3.5 h-3.5" /> Today's headlines</div>
        <div className="relative flex-1 overflow-hidden">
          <ul className="ticker-track flex items-center whitespace-nowrap py-2.5 group-hover:[animation-play-state:paused]" style={{ animationDuration: `${duration}s` }}>
            {loop.map((it, i) => (
              <li key={`${it.key}-${i}`} data-testid={i < items.length ? `ticker-item-${i + 1}` : undefined} className="flex items-center gap-2 pr-10 text-[13px] text-ink">
                <span className="font-bold text-gold">{it.year ?? ""}</span>
                {it.url ? <a href={it.url} target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-gold">{it.text}</a> : <span className={`font-semibold ${it.star ? "uppercase tracking-wide" : ""}`}>{it.text}</span>}
                <span className="text-[10px] uppercase tracking-wider text-ink-muted">{it.source}</span>
                <span className="text-gold">◆</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
